import React, { useContext } from 'react';
import classes from './CategoryTable.module.scss';
import classNames from 'classnames';
import { CategoriesUsageData } from './CategoryTable.controller';
import { CategoriesColorsEnum } from '../../../../../constants/categories-colors';
import { EmptyScreen } from '../../../LineChart/EmptyScreen';
import { AnalyticsContext } from '../../../AnalyticsProvider';
import { Metrics } from '../../helpers/chartHelpers';

type CategoryTableViewProps = {
  categoriesUsageData: CategoriesUsageData[];
  isEmpty: boolean;
  availableMetrics: Metrics[];
};

type CategoryLineProps = CategoriesUsageData & {
  hasLikes: boolean;
  hasComments: boolean;
  hasShares: boolean;
  isLastLine: boolean;
};
const CategoryLine = (props: CategoryLineProps) => {
  const {
    name,
    color,
    postCount,
    postPercentage,
    likePercentage,
    sharePercentage,
    commentsPercentage,
    commentsCount,
    likeCount,
    shareCount,
    hasLikes,
    hasComments,
    hasShares,
    isLastLine
  } = props;
  return (
    <div className={classes.LineContainer}>
      <div className={classNames(classes.Cell, { [classes.BorderBottomLeft]: isLastLine })}>
        <div className={classes.Category}>
          <span>{name}</span>
          <p className={classes.Value}>{postCount}</p>
        </div>

        <div className={classes.Bar}>
          <div
            style={{
              backgroundColor: CategoriesColorsEnum[color as keyof typeof CategoriesColorsEnum],
              width: `${isNaN(postPercentage ?? 0) ? 0 : postPercentage}%`
            }}></div>
        </div>
      </div>
      {hasLikes && (
        <div className={classes.Cell}>
          <p className={classes.Value}>{likeCount ?? 'no data'}</p>
          <div className={classes.Bar}>
            <div
              style={{ width: `${isNaN(likePercentage ?? 0) ? 0 : likePercentage}%` }}
              className={classes['BarLike']}></div>
          </div>
        </div>
      )}
      {hasComments && (
        <div className={classes.Cell}>
          <p className={classes.Value}>{commentsCount ?? 'no data'}</p>
          <div className={classes.Bar}>
            <div
              style={{ width: `${isNaN(commentsPercentage ?? 0) ? 0 : commentsPercentage}%` }}
              className={classes['BarComments']}></div>
          </div>
        </div>
      )}
      {hasShares && (
        <div className={classNames(classes.Cell, { [classes.BorderBottomRight]: isLastLine })}>
          <p className={classes.Value}>{shareCount ?? 'no data'}</p>
          <div className={classes.Bar}>
            <div
              style={{ width: `${isNaN(sharePercentage ?? 0) ? 0 : sharePercentage}%` }}
              className={classes['BarShares']}></div>
          </div>
        </div>
      )}
    </div>
  );
};
export const CategoryTableView = ({ categoriesUsageData, isEmpty, availableMetrics }: CategoryTableViewProps) => {
  const { selectedProfilesIds } = useContext(AnalyticsContext);
  if (isEmpty) {
    return <EmptyScreen noProfiles={selectedProfilesIds.length === 0} />;
  }
  const hasLikes = availableMetrics.includes(Metrics.likes);
  const hasShares = availableMetrics.includes(Metrics.shares);
  const hasComments = availableMetrics.includes(Metrics.comments);
  return (
    <div>
      <div className={classes.HeaderContainer}>
        <div className={classNames(classes.HeaderCell, classes.Cell)}>
          <span>Category</span>
        </div>
        {availableMetrics.map(metric => {
          return (
            <div key={metric} className={classNames(classes.HeaderCell, classes.Cell)}>
              <span>{metric}</span>
            </div>
          );
        })}
      </div>
      {categoriesUsageData.map((category, index) => (
        <CategoryLine
          key={category.categoryId}
          {...category}
          hasLikes={hasLikes}
          hasComments={hasComments}
          hasShares={hasShares}
          isLastLine={categoriesUsageData.length === index + 1}
        />
      ))}
    </div>
  );
};
